import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import type { ChatCompletionMessageParam } from '@adornis/llm/server/token-js/index.js';
import { jsonrepair } from 'jsonrepair';
import { LLM_PAYLOAD_SAFE_LIMIT_BYTES } from '../../../shared/constants.js';
import { getSoloLanguageUseRule, type LanguageUseSchemeLike } from '../../../shared/grammar-rules-resolution.js';
import { defaultGradingModel, defaultGradingTemperature } from '../../../shared/models.js';
import { Exam, type ExamTask } from '../../exam.js';
import {
  buildConsistencyReviewSystemPrompt,
  buildGradingSystemPrompt,
  buildLanguageUseSchemeGroupGradingSystemPrompt,
  buildLanguageUseOnlyTaskGradingSystemPrompt,
} from '../../prompts.js';
import { Student } from '../../student.js';
import type { SubmissionTask } from '../../submission.js';
import { Teacher } from '../../teacher.js';
import { evaluateCombinedGradingConfidence, evaluateGradingConfidence } from '../../utils/evaluateGradingConfidence.js';
import type { Grading } from '../../utils/grading-utils.js';
import { ownMaxPoints } from '../../utils/points-model.js';
import { appendTaskRefSegment, normalizeTaskRefSegment } from '../../utils/task-ref.js';
import { addCostToExam } from '../add-cost.js';
import { getConfiguredLLMRouter } from '../llm/llm-gate.js';
// Imported directly from the tool module (not the tools barrel) to avoid the barrel's
// grading-agent.tool → ... import cycle. memory.tool has no back-import here.
import { Conversation } from '../../conversation.js';
import { getTeacherMemoryInstructionBlock } from '../tools/memory.tool.js';
import {
  additionalInfoImageIds,
  type AdditionalInfoPromptItem,
  collectAllTaskAdditionalInfoIds,
  formatAdditionalInfoText,
  normalizeAdditionalInfoForPrompt,
  resolveAdditionalInfoForTask,
} from './additional-info-context.js';
import {
  type AgentPayloadPart,
  assembleAgentPayload,
  assembleHierarchyPayload,
  clampPayloadToByteLimit,
  type HierarchyTaskInput,
  imageIdsAsBoxes,
  type PayloadBox,
} from './agent-payload.js';
import { deriveAnswerText, type DetectedGrammarError } from './grammar-evaluation.js';

let _llmRouter: AdornisLLMRouter<any> | null = null;

export type GradeTaskTarget = 'content' | 'languageUse';

/** Content(+language-use) retry schema. `includeLanguageUse` omits the language-use keys for content-only calls. */
function buildGradingJsonSchema(includeLanguageUse: boolean) {
  const properties: Record<string, unknown> = {
    id: { type: 'string' },
    type: { type: 'string', enum: ['MAIN', 'SUB'] },
    grade: { type: 'number' },
    explanation: { type: 'string' },
    feedback: { type: 'string' },
  };
  const required = ['id', 'type', 'grade', 'explanation', 'feedback'];
  if (includeLanguageUse) {
    properties.languageUseGrade = { type: ['number', 'null'] };
    properties.languageUseExplanation = { type: ['string', 'null'] };
    required.push('languageUseGrade', 'languageUseExplanation');
  }
  return {
    name: 'grading_results',
    strict: true,
    schema: {
      type: 'object',
      additionalProperties: false,
      required: ['results'],
      properties: {
        results: { type: 'array', items: { type: 'object', additionalProperties: false, required, properties } },
      },
    },
  };
}

/** Language-use-only retry schema (target === 'languageUse') — no content keys at all. */
const LANGUAGE_USE_ONLY_JSON_SCHEMA = {
  name: 'language_use_only_grading_results',
  strict: true,
  schema: {
    type: 'object',
    additionalProperties: false,
    required: ['results'],
    properties: {
      results: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['id', 'type', 'languageUseGrade', 'languageUseExplanation'],
          properties: {
            id: { type: 'string' },
            type: { type: 'string', enum: ['MAIN', 'SUB'] },
            languageUseGrade: { type: 'number' },
            languageUseExplanation: { type: 'string' },
          },
        },
      },
    },
  },
};

// Map stored/hydrated bounding boxes to plain PayloadBoxes for the assembler (reads each field once,
// so it works for both raw docs and hydrated entities without tripping omitted-field getters).
function toPayloadBoxes(boxes: any): PayloadBox[] {
  return (Array.isArray(boxes) ? boxes : []).map(box => ({
    contentType: box?.contentType ?? null,
    ocrExtractedText: box?.ocrExtractedText ?? null,
    imageId: box?.imageId ?? null,
    isHandwritten: box?.isHandwritten ?? null,
  }));
}

interface ContextItem {
  llmId: string;
  dbId: string;
  mainDbId: string;
  type: 'MAIN' | 'SUB';
  mainTaskNumber: string;
  subTaskNumber?: string;
  nestingPath: string[];
  description: string;
  taskType: string;
  masterSolution: string;
  criteria: string;
  maxPoints: number;
  extractedStudentAnswer: string;
  // The real bounding boxes per category — the payload assembler renders each by its content type.
  descriptionBoxes: PayloadBox[];
  masterSolutionBoxes: PayloadBox[];
  gradingSchemeBoxes: PayloadBox[];
  studentAnswerBoxes: PayloadBox[];
  // Supporting "additional information" relevant to this task (text injected, images attached).
  additionalInformation: string;
  additionalInfoImageIds: string[];
  hasAnswer: boolean;
  requiresGrading: boolean;
  boundingBoxConfidences: number[];
  rawTaskRef: any;
  /** This node's solo (1-member) LanguageUseScheme rule text/max points, if any (see getSoloLanguageUseRule). */
  languageUseCriteria?: string;
  languageUseMaxPoints?: number;
  languageUseSchemeId?: string;
  /** Detected grammar/spelling errors for this task's answer, shown as evidence when languageUseCriteria is set. */
  grammarErrors?: DetectedGrammarError[];
}

interface GradeTaskGrammarContext {
  /** Detected grammar/spelling errors per task (keyed by ExamTask._id), for tasks with a solo language-use rule. */
  errorsByTaskId: Record<string, DetectedGrammarError[]>;
}

interface GradeTaskOptions {
  requestedTaskRefs?: Set<string> | null;
  examId?: string;
  /** Detected grammar errors for tasks with a solo language-use rule (see GradeTaskGrammarContext). */
  grammar?: GradeTaskGrammarContext;
  /** Every LanguageUseScheme on the exam — used to resolve a node's solo (1-member) rule, if any. */
  languageUseSchemeRules?: LanguageUseSchemeLike[] | null;
  /**
   * Which family to grade in this call. 'content' (default) strips language-use from the prompt/schema
   * entirely (cheapest, and never touches languageUse* fields on write). 'languageUse' strips content
   * entirely — a standalone language-use-only call for tasks with their own merged languageUseCriteria
   * (never touches content fields on write). A caller wanting both as two SEPARATE rows (e.g. a scoped
   * regrade of only what a teacher just edited) dispatches two calls — see gs-refine.job.ts's rationale
   * for why one row cannot carry both. `includeSoloLanguageUse` below is the other way to get both: same
   * row, same call.
   */
  target?: GradeTaskTarget;
  /**
   * Batches a solo (1-member) LanguageUseScheme rule into this SAME 'content' call instead of requiring a
   * separate 'languageUse' row — restores the old default-grading behavior for a genuine first grade of a
   * submission (no LLM call is doubled for the common case of a 1-member rule). Ignored when target is
   * already 'languageUse'. Only set by a caller that means "grade everything for the first time"
   * (run_grading_agent's own default dispatch, and a new_submissions regrade for a late-arriving
   * submission) — NOT by a scoped regrade dispatched because one specific scheme was just edited, which
   * must keep touching only the family that changed.
   */
  includeSoloLanguageUse?: boolean;
}

// Cap the injected error list so a pathological answer can't blow up the prompt.
const MAX_GRAMMAR_ERRORS_IN_PROMPT = 100;

/** Renders a task's detected grammar/spelling errors as evidence text for the languageUseScheme section. */
function formatGrammarErrorsText(errors: DetectedGrammarError[] | undefined): string {
  const list = Array.isArray(errors) ? errors : [];
  if (list.length === 0) {
    return "The specialized grammar checker found no grammar/spelling errors in the student's answer.";
  }
  const shown = list.slice(0, MAX_GRAMMAR_ERRORS_IN_PROMPT);
  const formatted = shown
    .map(
      (error, index) =>
        `[${index + 1}] (${error.category}) ${error.message}${error.context ? ` — context: ${JSON.stringify(error.context)}` : ''}`,
    )
    .join('\n');
  const omitted = list.length - shown.length;
  return `Grammar/spelling errors found in the student's answer by the specialized grammar checker (evidence — verify against the answer text and ignore clearly false positives):\n${formatted}${omitted > 0 ? `\n(${omitted} further errors omitted)` : ''}`;
}

function getLlmRouter(): AdornisLLMRouter<any> {
  if (!_llmRouter) {
    // The CONFIGURED form — model and temperature are set once here and `create` is called with only
    // messages, which needs the router that merges the two.
    _llmRouter = getConfiguredLLMRouter({
      provider: 'openrouter',
      model: defaultGradingModel,
      temperature: defaultGradingTemperature,
    });
  }
  return _llmRouter;
}

function toMessageText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map(part => (typeof part === 'object' && part !== null && 'text' in part ? String(part.text ?? '') : ''))
      .join('\n')
      .trim();
  }
  return '';
}

function parseGradeValue(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (typeof raw === 'string') {
    const normalized = raw.replace(',', '.').replace(/[^0-9.\-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function clampGrade(value: number, maxPoints: number): number {
  // 0 = no points assigned (model invariant): an unpointed task caps at 0, no silent default-to-1.
  const safeMax = Number.isFinite(maxPoints) && maxPoints > 0 ? maxPoints : 0;
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(value, safeMax));
}

function safeParseJson<T>(raw: string, fallback: T): T {
  try {
    // Strip markdown code fences that some models add despite json_object format
    const stripped = raw.replace(/^```(?:json)?\s*/m, '').replace(/\s*```\s*$/m, '');
    const firstBrace = stripped.indexOf('{');
    const firstBracket = stripped.indexOf('[');
    const start = Math.min(firstBrace !== -1 ? firstBrace : Infinity, firstBracket !== -1 ? firstBracket : Infinity);

    const lastBrace = stripped.lastIndexOf('}');
    const lastBracket = stripped.lastIndexOf(']');
    const end = Math.max(lastBrace, lastBracket);

    if (start !== Infinity && end !== -1 && end >= start) {
      const jsonString = stripped.substring(start, end + 1);
      try {
        return JSON.parse(jsonString) as T;
      } catch {
        return JSON.parse(jsonrepair(jsonString)) as T;
      }
    }

    logger.warn({ raw, context: 'safeParseJson' }, 'Kein gültiges JSON im Text gefunden');
    return fallback;
  } catch (e) {
    logger.error({ err: e, rawSnippet: raw.slice(0, 500) }, `[safeParseJson] Fehler beim Parsen:`);
    return fallback;
  }
}

function getMainTaskScheme(task: ExamTask): { criteria: string; maxPoints: number } {
  const gradingScheme = (task.gradingScheme as any)?.doc || task.gradingScheme;
  const overallGradingScheme = (task as any).overallGradingScheme?.doc || (task as any).overallGradingScheme;
  const source = gradingScheme || overallGradingScheme || {};

  return {
    criteria: typeof source.criteria === 'string' ? source.criteria : 'Richtigkeit der Antwort.',
    // The main task's OWN points from the single source of truth (0 when unpointed — no default-to-1).
    maxPoints: ownMaxPoints(task),
  };
}

export async function gradeTask(
  task: ExamTask,
  taskSubmission: SubmissionTask,
  options?: GradeTaskOptions,
): Promise<Grading[]> {
  const startTime = Date.now();
  const examId = options?.examId;
  const target: GradeTaskTarget = options?.target ?? 'content';
  let userLanguage = 'English';
  if (examId) {
    try {
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ examId });
      if (conversation && (conversation as any).language) {
        userLanguage = (conversation as any).language;
      }
    } catch (convErr) {
      logger.error(
        { err: convErr, examId: String(examId), context: 'gradeTask' },
        'Failed to fetch conversation for exam',
      );
    }
  }
  // Load the exam-wide "additional information" (supporting materials) so each task can be graded with
  // the formula sheets / diagrams / shared tables it depends on. Needs the full task tree to decide
  // which items are globally shared vs task-linked. Degrades gracefully when the exam can't be loaded.
  let allAdditionalInfo: AdditionalInfoPromptItem[] = [];
  let allTaskAdditionalInfoIds = new Set<string>();
  // Teacher's stored grading/feedback preferences (subject-filtered), injected into the grading prompt.
  let teacherGradingRules = '';
  let gradingKindness = 'balanced';
  // Whether this exam's owner is grading their own submission (a Student self-upload) rather than a
  // teacher grading a class — same "whichever profile exists for this userID" resolution as
  // useCurrentUserProfile.ts / generate-answer.ts, just keyed on the exam owner instead of the
  // current session user (grading runs in a background job, with no session to read).
  let isStudentSelfGrading = false;
  if (examId) {
    try {
      const examCollection = await getCollection(Exam);
      const examDoc = await examCollection.findOne({ _id: examId });
      allAdditionalInfo = normalizeAdditionalInfoForPrompt((examDoc as any)?.additionalInformation);
      allTaskAdditionalInfoIds = collectAllTaskAdditionalInfoIds(examDoc?.tasks as any[]);
      const teacherUserId = (examDoc as any)?.userID;
      teacherGradingRules = await getTeacherMemoryInstructionBlock({
        userID: teacherUserId,
        category: 'grading_and_feedback',
        subject: (examDoc as any)?.subject,
      });
      if (teacherUserId) {
        const teacherCollection = await getCollection(Teacher);
        const teacherDoc = await teacherCollection.findOne({ userID: teacherUserId });
        if ((teacherDoc as any)?.gradingKindness) {
          gradingKindness = (teacherDoc as any).gradingKindness;
        }
        const studentCollection = await getCollection(Student);
        const studentDoc = await studentCollection.findOne({ userID: teacherUserId });
        isStudentSelfGrading = Boolean(studentDoc);
      }
    } catch (e) {
      logger.error(e, `[gradeTask] Failed to load additional information for exam ${String(examId)}`);
    }
  }

  const mainTaskRef = normalizeTaskRefSegment(task.taskNumber);
  const requestedTaskRefs = options?.requestedTaskRefs ?? null;
  const hasRequestedFilter = Boolean(requestedTaskRefs && requestedTaskRefs.size > 0);

  const mainTaskScheme = getMainTaskScheme(task);

  const contextItems: ContextItem[] = [];
  const finalGradings: Grading[] = [];

  const mainDbIdStr = String((task as any)._id || '');

  // Recursive walker to collect all tasks for grading (handles arbitrary depth)
  const walkTask = (
    currentTask: ExamTask,
    currentSubmission: SubmissionTask | undefined,
    isMainTask: boolean,
    parentTaskNumber: string,
    nestingPath: string[] = [],
    accumulatedRef: string = mainTaskRef,
    // Set of combined refs of every ancestor node visited on the path from root to this node.
    // Used for the descendant check so we never rely on string startsWith (which is ambiguous
    // with Roman numerals: "2iii".startsWith("2ii") === true even though they are siblings).
    ancestorRefs: Set<string> = new Set([mainTaskRef]),
  ) => {
    const currentTaskRef = normalizeTaskRefSegment(currentTask.taskNumber);
    const currentCombinedRef = isMainTask ? mainTaskRef : appendTaskRefSegment(accumulatedRef, currentTaskRef);
    const currentNeedsAnswer = (currentTask as any).needsAnswer !== false;

    const shouldGradeByFilter =
      !hasRequestedFilter ||
      requestedTaskRefs!.has(currentCombinedRef) ||
      // descendant check: grade this task if any requested ref is an actual ancestor
      // (present in the path from root to this task), not just a string prefix.
      Array.from(requestedTaskRefs!).some(ref => ancestorRefs.has(ref));

    const ocrText = (currentSubmission?.boundingBoxes ?? [])
      .map(b => (b.ocrExtractedText ?? '').trim())
      .filter(Boolean)
      .join(' ')
      .trim();
    const studentAnswer = (currentSubmission?.studentAnswer ?? '').trim();
    const extracted = ocrText || studentAnswer;
    const numBoxes = currentSubmission?.boundingBoxes?.length || 0;
    const hasAnswer =
      numBoxes > 0 ||
      (typeof currentSubmission?.studentAnswer === 'string' && currentSubmission.studentAnswer.trim().length > 0);

    // Get grading scheme for this task level
    const scheme = (currentTask.gradingScheme as any)?.doc || currentTask.gradingScheme || {};
    // This node's OWN points from the single source of truth (leaf: scheme→declared; answerable parent: own).
    // No inherit-from-main fallback and no default-to-1: an unpointed node is worth 0.
    const maxPoints = ownMaxPoints(currentTask);
    const criteria =
      typeof scheme.criteria === 'string' && scheme.criteria.trim().length > 0
        ? scheme.criteria
        : mainTaskScheme.criteria;

    // MS/GS boxes use this task's OWN boxes (no fallback to the main task, to avoid attaching the same
    // reference image to every subtask and bloating the payload). Hoisted out of the payload literal
    // below because the master-solution TEXT depends on whether there is a box to look at.
    const masterSolutionBoxes = toPayloadBoxes(
      ((currentTask.masterSolution as any)?.doc || currentTask.masterSolution)?.boundingBoxes,
    );
    const gradingSchemeBoxes = toPayloadBoxes(
      ((currentTask.gradingScheme as any)?.doc || currentTask.gradingScheme)?.boundingBoxes,
    );
    const masterSolutionText = currentTask.masterSolution?.content || task.masterSolution?.content || '';

    const llmId = isMainTask ? currentTask.taskNumber : `${parentTaskNumber}_${currentTask.taskNumber}`;

    const currentNestingPath = isMainTask ? [] : [...nestingPath, currentTask.taskNumber];
    const requiresGrading = currentNeedsAnswer && shouldGradeByFilter;

    // Solo (1-member) LanguageUseScheme rule for this node — omitted entirely for target 'content' so the
    // rest of the pipeline (schema, prompt, hierarchy materials) behaves exactly as if it didn't exist.
    // A rule with 2+ members is graded separately via gradeLanguageUseSchemeGroup, not here.
    const qualifiedTaskNumber = [task.taskNumber, ...currentNestingPath].join('|');
    const soloRule =
      target === 'languageUse' || options?.includeSoloLanguageUse
        ? getSoloLanguageUseRule(qualifiedTaskNumber, options?.languageUseSchemeRules)
        : null;
    const languageUseCriteriaRaw = soloRule?.criteria ?? '';
    const languageUseMaxPointsValue = languageUseCriteriaRaw ? soloRule!.maxPoints || 1 : 0;
    const dbId = String((currentTask as any)._id || '');

    // Always include the task in prompt context so the LLM sees full hierarchy/context.
    contextItems.push({
      llmId,
      dbId: String((currentTask as any)._id || ''),
      mainDbId: mainDbIdStr,
      type: isMainTask ? 'MAIN' : 'SUB',
      mainTaskNumber: task.taskNumber,
      subTaskNumber: isMainTask ? undefined : currentTask.taskNumber,
      nestingPath: currentNestingPath,
      description: currentTask.description || 'Keine Beschreibung',
      taskType: (currentTask as any).taskType || 'DEFAULT',
      // Same rule as the student answer below: only say "Keine Musterlösung" when there is truly nothing.
      // An image-only master solution (a sketch or drawing) has no transcribable text but its image IS
      // sent below the master-solution header, so emit empty text here — claiming there is no master
      // solution while showing the grader one is contradictory input.
      masterSolution: masterSolutionText || (masterSolutionBoxes.length > 0 ? '' : 'Keine Musterlösung'),
      criteria,
      maxPoints,
      // Only say "Keine Antwort" when there is truly nothing. An image-only answer has no OCR text but
      // its image IS sent below the answer header, so emit empty text here (not "No answer", which would
      // wrongly push the grader toward 0).
      extractedStudentAnswer: extracted || (numBoxes > 0 ? '' : 'Keine Antwort'),
      // Real boxes per category — the assembler renders each by content type. The student answer uses
      // the submission's own boxes (so a handwritten text box sends its transcription, an image/drawing
      // sends its image). MS/GS boxes are resolved above.
      studentAnswerBoxes: toPayloadBoxes(currentSubmission?.boundingBoxes),
      descriptionBoxes: toPayloadBoxes((currentTask as any)?.boundingBoxes),
      masterSolutionBoxes,
      gradingSchemeBoxes,
      ...(() => {
        const relevant = resolveAdditionalInfoForTask(currentTask, allAdditionalInfo, allTaskAdditionalInfoIds);
        return {
          additionalInformation: formatAdditionalInfoText(relevant),
          additionalInfoImageIds: additionalInfoImageIds(relevant),
        };
      })(),
      hasAnswer,
      requiresGrading,
      boundingBoxConfidences: currentSubmission?.boundingBoxes?.map(bb => Number((bb as any).confidence) || 0) || [],
      rawTaskRef: currentTask,
      ...(languageUseCriteriaRaw
        ? {
            languageUseCriteria: languageUseCriteriaRaw,
            languageUseMaxPoints: languageUseMaxPointsValue,
            languageUseSchemeId: soloRule!.schemeId,
            grammarErrors: options?.grammar?.errorsByTaskId?.[dbId],
          }
        : {}),
    });

    // Auto-grade only those tasks that should be graded but have no answer.
    if (requiresGrading && !hasAnswer) {
      if (target === 'languageUse') {
        // Language-use-only mode: only push a zero fallback for items that actually have a rule.
        if (languageUseCriteriaRaw) {
          finalGradings.push({
            startTime,
            endTime: Date.now(),
            answerID: llmId,
            maxPoints: languageUseMaxPointsValue,
            mainTaskNumber: task.taskNumber,
            subTaskNumber: isMainTask ? undefined : currentTask.taskNumber,
            dbId,
            mainDbId: mainDbIdStr,
            taskType: isMainTask ? 'MAIN' : 'SUB',
            grade: 0,
            explanation: '',
            feedback: '',
            confidence: 1,
            reviewerFeedback: 'Automatische 0-Punkte-Bewertung, da Antwortfeld leer.',
            rawConfidence: null as any,
            rawAPIResponse: '{"languageUseGrade": 0}',
            nestingPath: currentNestingPath,
            languageUseGrade: 0,
            languageUseExplanation: 'Der Schüler hat keine Antwort gegeben oder die Antwortbox wurde leer gelassen.',
            languageUseConfidence: 1,
            languageUseReviewerFeedback: 'Automatische 0-Punkte-Bewertung, da Antwortfeld leer.',
            languageUseSchemeId: soloRule!.schemeId,
          } as any);
        }
      } else {
        finalGradings.push({
          startTime,
          endTime: Date.now(),
          answerID: llmId, // Use full llmId to preserve nesting
          maxPoints,
          mainTaskNumber: task.taskNumber,
          subTaskNumber: isMainTask ? undefined : currentTask.taskNumber,
          dbId,
          mainDbId: mainDbIdStr,
          taskType: isMainTask ? 'MAIN' : 'SUB',
          grade: 0,
          explanation: 'Der Schüler hat keine Antwort gegeben oder die Antwortbox wurde leer gelassen.',
          feedback: 'Leider keine Antwort eingetragen.',
          confidence: 1,
          reviewerFeedback: 'Automatische 0-Punkte-Bewertung, da Antwortfeld leer.',
          rawConfidence: null as any,
          rawAPIResponse: '{"grade": 0}',
          nestingPath: currentNestingPath,
          ...(languageUseCriteriaRaw
            ? {
                languageUseGrade: 0,
                languageUseExplanation: 'Der Schüler hat keine Antwort gegeben oder die Antwortbox wurde leer gelassen.',
                languageUseConfidence: 1,
                languageUseReviewerFeedback: 'Automatische 0-Punkte-Bewertung, da Antwortfeld leer.',
                languageUseSchemeId: soloRule!.schemeId,
              }
            : {}),
        } as any);
      }
    }

    // Recursively process subtasks
    const subTasks = ((currentTask as any).subTasks ?? []) as ExamTask[];
    if (Array.isArray(subTasks) && subTasks.length > 0) {
      const subSubmissionMap = new Map(
        (currentSubmission as any)?.subTasksSubmission?.map((s: any) => [s.taskNumber, s as SubmissionTask]) || [],
      );

      for (const subTask of subTasks) {
        const subSubmission = subSubmissionMap.get(subTask.taskNumber) as SubmissionTask | undefined;
        const subNestingPath = isMainTask ? [] : [...nestingPath, currentTask.taskNumber];
        walkTask(
          subTask,
          subSubmission,
          false,
          llmId,
          subNestingPath,
          currentCombinedRef,
          new Set([...ancestorRefs, currentCombinedRef]),
        );
      }
    }
  };

  // Start recursion from main task
  walkTask(task, taskSubmission, true, '');

  const llmTargets: ContextItem[] = [];

  for (const item of contextItems) {
    if (!item.requiresGrading || !item.hasAnswer) continue;
    // Language-use-only calls only ever target items that actually carry a rule — other items
    // have nothing to send back and would otherwise show up as spurious "missing result" warnings.
    if (target === 'languageUse' && !item.languageUseCriteria) continue;
    llmTargets.push(item);
  }

  if (llmTargets.length === 0) {
    return finalGradings;
  }

  // Extract unique task types involved in this main+subtask hierarchy
  const uniqueTaskTypes = Array.from(
    new Set(contextItems.map(item => item.rawTaskRef?.taskType).filter(Boolean)),
  ) as string[]; // Cast to TaskType[] if applicable

  const hasLanguageUseEligibleTasks = contextItems.some(item => !!item.languageUseCriteria);

  const systemPrompt =
    target === 'languageUse'
      ? buildLanguageUseOnlyTaskGradingSystemPrompt(
          llmTargets.map(t => t.llmId),
          userLanguage,
        )
      : buildGradingSystemPrompt(
          {
            targetIds: llmTargets.map(t => t.llmId),
            taskTypes: uniqueTaskTypes,
            teacherRules: teacherGradingRules,
            hasLanguageUseEligibleTasks,
            gradingKindness,
            isStudentSelfGrading,
          },
          userLanguage,
        );

  // Build the full per-task context for the user message: every task is introduced by a header naming
  // its id, then its materials follow as labeled, interleaved text+image parts (the assembler resolves
  // and attaches images). Graded targets carry the master solution, grading scheme and the student's
  // answer (answer images always shown); context-only ancestors carry their description + answer text
  // so follow-through reasoning has the full hierarchy. Images are resized for this call if oversized.
  // For target 'languageUse', content materials (MS/GS/maxPoints) are omitted entirely — only the
  // language-use material is sent, so the call is genuinely content-free, not just content-ignored.
  const hierarchyTasks: HierarchyTaskInput[] = contextItems.map(item => {
    const task: HierarchyTaskInput = {
      id: item.llmId,
      hierarchy: item.type,
      taskType: item.taskType,
      // Pass the real boxes + a text fallback (the description string is used only if the boxes carry
      // no text, e.g. an image-only question). Additional info exposes ids, so wrap them as image boxes.
      taskDescription: { boxes: item.descriptionBoxes, text: item.description },
      additionalInformation: { boxes: imageIdsAsBoxes(item.additionalInfoImageIds), text: item.additionalInformation },
      studentAnswer: { boxes: item.studentAnswerBoxes, text: item.extractedStudentAnswer },
    };
    if (item.requiresGrading) {
      if (target !== 'languageUse') {
        task.maxPoints = item.maxPoints;
        task.masterSolution = { boxes: item.masterSolutionBoxes, text: item.masterSolution };
        task.gradingScheme = { boxes: item.gradingSchemeBoxes, text: item.criteria, maxPoints: item.maxPoints };
      }
      if (item.languageUseCriteria) {
        const errorsText = formatGrammarErrorsText(item.grammarErrors);
        task.languageUseScheme = {
          boxes: [],
          text: `${item.languageUseCriteria}\n\n${errorsText}`,
          maxPoints: item.languageUseMaxPoints,
        };
      }
    }
    return task;
  });

  const contextParts = await clampPayloadToByteLimit(
    await assembleHierarchyPayload(hierarchyTasks, { imageDetail: 'high' }),
    LLM_PAYLOAD_SAFE_LIMIT_BYTES,
  );

  const userPrompt = `Please grade the requested task IDs based on the full context provided below. Please answer in ${userLanguage}.`;

  const userMessageContent: any =
    contextParts.length > 0 ? [{ type: 'text', text: userPrompt }, ...contextParts] : userPrompt;

  const messages: ChatCompletionMessageParam[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userMessageContent },
  ];
  // logger.info('systemPrompt: ' + systemPrompt);
  // logger.info('userPrompt: ' + userMessageContent);
  // --- 5. LLM CALL & PARSING WITH FALLBACK PROTECTION ---
  let analysisResponse: any = null;
  try {
    analysisResponse = await getLlmRouter().chat.completions.create({
      response_format: { type: 'json_object' },
      messages,
    });
    logger.info(
      { cost: JSON.stringify((analysisResponse?.usage as any)?.cost), context: 'COST_TRACKING' },
      'DefaultGrading Agent cost',
    );
    await addCostToExam(analysisResponse, examId, 'defaultGrading-initial');
  } catch (initialLlmError) {
    logger.error(
      { err: initialLlmError, examId: String(examId), context: 'gradeTask' },
      'Primary completion invocation experienced a failure for exam',
    );
  }

  const analysisResult = analysisResponse ? toMessageText(analysisResponse.choices[0]?.message.content) : '';
  interface GradingResults {
    results?: Array<{
      id?: unknown;
      taskId?: unknown;
      type?: unknown;
      taskType?: unknown;
      grade?: unknown;
      explanation?: unknown;
      feedback?: unknown;
      languageUseGrade?: unknown;
      languageUseExplanation?: unknown;
    }>;
  }
  let parsedCombined = safeParseJson<GradingResults>(analysisResult, {});

  if (parsedCombined.results === undefined) {
    logger.warn(
      { taskIds: JSON.stringify(llmTargets.map(t => t.llmId)), context: 'gradeTask' },
      'First parse failed or empty response context, running schema retry for tasks',
    );
    try {
      const retryResponse = await getLlmRouter().chat.completions.create({
        response_format: {
          type: 'json_schema',
          json_schema:
            target === 'languageUse' ? LANGUAGE_USE_ONLY_JSON_SCHEMA : buildGradingJsonSchema(hasLanguageUseEligibleTasks),
        } as any,
        messages,
      });
      logger.info(
        { cost: JSON.stringify((retryResponse?.usage as any)?.cost), context: 'COST_TRACKING' },
        'DefaultGrading Retry cost',
      );
      await addCostToExam(retryResponse, examId, 'defaultGrading-retry');

      const retryResult = toMessageText(retryResponse.choices[0]?.message.content);
      parsedCombined = safeParseJson<GradingResults>(retryResult, {});
    } catch (retryLlmError) {
      logger.error({ err: retryLlmError, context: 'gradeTask' }, 'Balanced schema fallback retry execution crashed');
    }
  }

  if (!parsedCombined || !Array.isArray(parsedCombined.results)) {
    logger.error(
      { context: 'gradeTask' },
      'Critical: Upstream model connections exhausted or failed completely. Aborting data calculation processing loops.',
    );
    return finalGradings;
  }

  const normalizedResults = (parsedCombined.results ?? []).filter(
    item => item.id !== undefined || item.taskId !== undefined,
  );

  const llmTargetIds = llmTargets.map(target => String(target.llmId));
  const normalizedResultIds = normalizedResults.map(result => String(result.id || result.taskId));
  const unmatchedTargetIds = llmTargetIds.filter(targetId => !normalizedResultIds.includes(targetId));
  const unknownResultIds = normalizedResultIds.filter(resultId => !llmTargetIds.includes(resultId));

  if (unmatchedTargetIds.length > 0 || unknownResultIds.length > 0) {
    logger.warn(
      {
        mainTaskNumber: task.taskNumber,
        requestedTaskRefs: requestedTaskRefs ? Array.from(requestedTaskRefs) : null,
        llmTargetIds,
        normalizedResultIds,
        unmatchedTargetIds,
        unknownResultIds,
        context: 'gradeTask',
      },
      'LLM result/task mismatch detected',
    );
  }

  const llmGradings = await Promise.all(
    normalizedResults.map(async result => {
      try {
        const resultId = String(result.id || result.taskId);
        const item = llmTargets.find(i => String(i.llmId) === resultId);

        if (!item) {
          logger.warn(
            {
              mainTaskNumber: task.taskNumber,
              resultId,
              availableTargetIds: llmTargetIds,
              context: 'gradeTask',
            },
            'Ignoring unknown result id from LLM response',
          );
          return null;
        }

        const explanation = typeof result.explanation === 'string' ? result.explanation : '';
        const feedback = typeof result.feedback === 'string' ? result.feedback : '';
        const rawGrade = parseGradeValue(result.grade);
        const hasLanguageUse = !!item.languageUseCriteria;
        const languageUseExplanation =
          hasLanguageUse && typeof result.languageUseExplanation === 'string' ? result.languageUseExplanation : '';
        const grade = clampGrade(rawGrade, item.maxPoints);
        const languageUseGrade = clampGrade(parseGradeValue(result.languageUseGrade), item.languageUseMaxPoints ?? 1);

        let confidenceEval: { result: number; languageUseResult?: number; reviewerFeedback: string } = {
          result: 0.5,
          reviewerFeedback: 'Automatischer Bewertungskontext-Ausfall',
        };
        try {
          let reviewerPayloadParts: AgentPayloadPart[] = [];
          try {
            reviewerPayloadParts = await assembleAgentPayload({
              taskDescription: { boxes: item.descriptionBoxes, text: item.description },
              additionalInformation: {
                boxes: imageIdsAsBoxes(item.additionalInfoImageIds),
                text: item.additionalInformation,
              },
              ...(target !== 'languageUse'
                ? {
                    masterSolution: { boxes: item.masterSolutionBoxes, text: item.masterSolution },
                    gradingScheme: { boxes: item.gradingSchemeBoxes, text: item.criteria, maxPoints: item.maxPoints },
                  }
                : {}),
              studentAnswer: { boxes: item.studentAnswerBoxes, text: item.extractedStudentAnswer },
              imageDetail: 'high',
            });
          } catch (payloadError) {
            logger.warn(
              { err: payloadError, context: 'gradeTask' },
              'Failed to assemble reviewer payload, falling back to text',
            );
          }
          if (target === 'languageUse') {
            // Content-free reviewer pass — mirrors gradeLanguageUseSchemeGroup's standalone pattern: a
            // synthetic task description naming the language-use criteria, no master solution.
            const syntheticTask = {
              description: `Grammar/spelling assessment of task ${item.mainTaskNumber}${item.subTaskNumber ? `.${item.subTaskNumber}` : ''}. Criteria: ${item.languageUseCriteria}`,
            } as any;
            const languageUseOnlyEval = await evaluateGradingConfidence(
              syntheticTask,
              languageUseExplanation,
              {
                bbConfidences: item.boundingBoxConfidences,
                studentText: item.extractedStudentAnswer,
                masterSolutionContent: '',
                grade: languageUseGrade,
                maxPoints: item.languageUseMaxPoints ?? 1,
                agentPayloadParts: reviewerPayloadParts,
              },
              examId,
            );
            confidenceEval = {
              result: languageUseOnlyEval.result,
              languageUseResult: languageUseOnlyEval.result,
              reviewerFeedback: languageUseOnlyEval.reviewerFeedback,
            };
          } else {
            confidenceEval = hasLanguageUse
              ? await evaluateCombinedGradingConfidence(
                  item.rawTaskRef,
                  explanation,
                  { criteria: item.languageUseCriteria!, explanation: languageUseExplanation },
                  {
                    bbConfidences: item.boundingBoxConfidences,
                    studentText: item.extractedStudentAnswer,
                    masterSolutionContent: item.masterSolution,
                    agentPayloadParts: reviewerPayloadParts,
                  },
                  examId,
                )
              : await evaluateGradingConfidence(
                  item.rawTaskRef,
                  explanation,
                  {
                    bbConfidences: item.boundingBoxConfidences,
                    studentText: item.extractedStudentAnswer,
                    masterSolutionContent: item.masterSolution,
                    grade,
                    maxPoints: item.maxPoints,
                    agentPayloadParts: reviewerPayloadParts,
                  },
                  examId,
                );
          }
        } catch (confidenceException) {
          logger.error(
            { err: confidenceException, resultId, context: 'gradeTask' },
            'evaluateGradingConfidence operation crashed on element item',
          );
        }

        return {
          startTime,
          endTime: Date.now(),
          answerID: item.llmId, // Use full llmId to preserve nesting like "Aufgabe 1_a_(I)"
          maxPoints: item.maxPoints,
          mainTaskNumber: item.mainTaskNumber,
          subTaskNumber: item.subTaskNumber,
          dbId: item.dbId,
          mainDbId: item.mainDbId,
          taskType: String(result.type || result.taskType || item.type),
          // Content fields are meaningless placeholders under target 'languageUse' — the caller
          // (gradingAgent.ts) knows from its own `target` to ignore them, not infer per-item.
          feedback: target === 'languageUse' ? '' : feedback,
          explanation: target === 'languageUse' ? '' : explanation,
          grade: target === 'languageUse' ? 0 : grade,
          confidence: target === 'languageUse' ? 0 : confidenceEval.result,
          reviewerFeedback: target === 'languageUse' ? undefined : confidenceEval.reviewerFeedback,
          rawConfidence: (confidenceEval as any).raw as any,
          rawAPIResponse: analysisResult,
          nestingPath: item.nestingPath, // Include full nesting path
          ...(hasLanguageUse
            ? {
                languageUseGrade,
                languageUseExplanation,
                languageUseConfidence: confidenceEval.languageUseResult ?? confidenceEval.result,
                languageUseReviewerFeedback: confidenceEval.reviewerFeedback,
                languageUseSchemeId: item.languageUseSchemeId,
              }
            : {}),
        } as any;
      } catch (innerTaskMappingError) {
        logger.error(
          { err: innerTaskMappingError, context: 'gradeTask' },
          'Safe container intercepted unexpected processing exception on individual array map thread entry',
        );
        return null;
      }
    }),
  );

  finalGradings.push(
    ...llmGradings.filter(
      (
        g,
      ): g is Grading & {
        taskType: string;
        dbId: string;
        mainDbId: string;
        mainTaskNumber: string;
        subTaskNumber?: string;
      } => g !== null,
    ),
  );

  return finalGradings;
}

export interface LanguageUseSchemeGroupMember {
  examTask: ExamTask;
  submissionTask: SubmissionTask;
}

/**
 * Grades ONLY grammar/spelling for a multi-task LanguageUseScheme group: gathers the student-answer text
 * across every linked task (a split essay/poetry answer), grades it as one combined piece of writing
 * against the group's criteria, then runs the standalone reviewer pass. Content grading of the member
 * tasks is entirely separate (each task's own gradeTask call) — this never touches a task's own
 * GradingResult; the caller persists the result once, referenced by the group (see gradingAgent.ts).
 */
export async function gradeLanguageUseSchemeGroup(
  scheme: { _id: string; criteria: string; maxPoints: number },
  memberTasks: LanguageUseSchemeGroupMember[],
  options: { examId?: string },
): Promise<{
  score: number;
  explanation: string;
  confidence: number;
  reviewerFeedback?: string;
  rawAPIResponse: string;
}> {
  const examId = options.examId;
  let userLanguage = 'English';
  if (examId) {
    try {
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ examId });
      if (conversation && (conversation as any).language) {
        userLanguage = (conversation as any).language;
      }
    } catch (convErr) {
      logger.error(
        { err: convErr, examId: String(examId), context: 'gradeLanguageUseSchemeGroup' },
        'Failed to fetch conversation for exam',
      );
    }
  }

  const combinedText = memberTasks
    .map(member => `[Aufgabe ${member.examTask.taskNumber}]\n${deriveAnswerText(member.submissionTask)}`)
    .filter(part => part.trim().length > 0)
    .join('\n\n');
  const combinedBoxes = memberTasks.flatMap(member => toPayloadBoxes(member.submissionTask?.boundingBoxes));
  const allBbConfidences = memberTasks.flatMap(
    member => member.submissionTask?.boundingBoxes?.map(bb => Number((bb as any).confidence) || 0) || [],
  );

  const systemPrompt = buildLanguageUseSchemeGroupGradingSystemPrompt(scheme.criteria, scheme.maxPoints, userLanguage);
  const payloadParts = await clampPayloadToByteLimit(
    await assembleAgentPayload({
      studentAnswer: { boxes: combinedBoxes, text: combinedText || 'Keine Antwort' },
      imageDetail: 'high',
    }),
    LLM_PAYLOAD_SAFE_LIMIT_BYTES,
  );
  const userPrompt = `Please grade the grammar/spelling of the combined answer below. Please answer in ${userLanguage}.`;
  const userMessageContent: any =
    payloadParts.length > 0 ? [{ type: 'text', text: userPrompt }, ...payloadParts] : userPrompt;

  const messages: ChatCompletionMessageParam[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userMessageContent },
  ];

  let analysisResponse: any = null;
  try {
    analysisResponse = await getLlmRouter().chat.completions.create({
      response_format: { type: 'json_object' },
      messages,
    });
    await addCostToExam(analysisResponse, examId, 'defaultGrading-languageUseGroup');
  } catch (err) {
    logger.error(
      { err, examId: String(examId), schemeId: scheme._id, context: 'gradeLanguageUseSchemeGroup' },
      'Language-use group grading call failed',
    );
  }

  const analysisResult = analysisResponse ? toMessageText(analysisResponse.choices[0]?.message.content) : '';
  interface LanguageUseGroupResult {
    results?: Array<{ grade?: unknown; explanation?: unknown }>;
  }
  const parsed = safeParseJson<LanguageUseGroupResult>(analysisResult, {});
  const first = Array.isArray(parsed.results) ? parsed.results[0] : undefined;
  const explanation = typeof first?.explanation === 'string' ? first.explanation : '';
  const score = clampGrade(parseGradeValue(first?.grade), scheme.maxPoints);

  const syntheticTask = {
    description: `Grammar/spelling assessment of a combined answer spanning tasks ${memberTasks
      .map(m => m.examTask.taskNumber)
      .join(', ')}. Criteria: ${scheme.criteria}`,
  } as any;

  let confidenceEval = { result: 0.5, reviewerFeedback: 'Automatischer Bewertungskontext-Ausfall' };
  try {
    confidenceEval = await evaluateGradingConfidence(
      syntheticTask,
      explanation,
      {
        bbConfidences: allBbConfidences,
        studentText: combinedText,
        masterSolutionContent: '',
        grade: score,
        maxPoints: scheme.maxPoints,
        agentPayloadParts: payloadParts,
      },
      examId,
    );
  } catch (confidenceException) {
    logger.error(
      { err: confidenceException, schemeId: scheme._id, context: 'gradeLanguageUseSchemeGroup' },
      'evaluateGradingConfidence failed for language-use group',
    );
  }

  return {
    score,
    explanation,
    confidence: confidenceEval.result,
    reviewerFeedback: confidenceEval.reviewerFeedback,
    rawAPIResponse: analysisResult,
  };
}

/**
 * One already-graded node (main task or subtask) for one submission, collected by the caller
 * (gradingAgent.ts's gradeOneTask) right after its first-pass grade was computed — before or after
 * that first-pass value is written, since `reviewGradingConsistencyForTask` never reads the DB itself.
 * `gradingResultFieldPath`/`arrayFilters` are the exact dot-path + Mongo arrayFilters needed to $set
 * this one node's `gradingResult` sub-fields directly, so the consistency writer never has to re-derive
 * addressing logic that gradeOneTask already built for its own first-pass write.
 */
export interface ConsistencyReviewInput {
  submissionId: string;
  /** The ExamTask/subtask node's own persistent id — shared by every student's entry for this node. */
  dbId: string;
  /** Human-readable task/subtask number, shown to the LLM (e.g. "3" or "a"). */
  nodeLabel: string;
  criteria: string;
  score: number;
  maxPoints: number;
  explanation: string;
  studentAnswerExcerpt: string;
  /**
   * WHAT THIS ENTRY IS FOR. A `target` may be adjusted; a `reference` may only be compared against.
   *
   * References are how a wave that regrades a handful of submissions is still calibrated against the exam: the
   * cohort's existing grades establish the standard, the wave's own grades are brought into line with it, and
   * nothing outside the wave is touched. Without them a three-submission wave could only be made consistent with
   * ITSELF, which pulls those three away from the 397 they sit among — the one outcome worse than not reviewing.
   *
   * Absent means `target`, so every existing caller keeps its behaviour.
   */
  role?: 'target' | 'reference';
  /**
   * Where an adjustment to this entry would be written. Absent on a REFERENCE, because a reference is never
   * written — which is also what lets one be built from stored grades without rebuilding the write coordinates
   * the grading pass derives as it goes.
   */
  gradingResultFieldPath?: string;
  arrayFilters?: Record<string, unknown>[];
}

export interface ConsistencyAdjustment {
  submissionId: string;
  gradingResultFieldPath: string;
  arrayFilters: Record<string, unknown>[];
  score: number;
  explanation: string;
}

// Truncate student-answer excerpts embedded in the consistency-review prompt — this call already
// bundles every student's entry for a task into one request, so per-entry payload must stay small.
const CONSISTENCY_REVIEW_ANSWER_EXCERPT_LIMIT = 500;

/**
 * Second-pass "consistency reviewer" for a task, run once per task AFTER every submission's first-pass
 * grade for it has been computed (see gradeOneTask in gradingAgent.ts). Compares every student's score +
 * explanation for the SAME node (grouped by `dbId`) and proposes the minimal set of corrections needed to
 * make grading consistent across students — deliberately including nodes whose grade was previously
 * approved by a teacher, per the product decision that approval status does not exempt a node from a
 * consistency re-check (the caller is responsible for resetting `isApproved` on any node it adjusts).
 *
 * Bounded to one LLM call per task per run regardless of student count: nodes with fewer than 2 entries
 * (nothing to compare) are skipped locally, and every remaining node for the task is compared in the same
 * call rather than one call per node.
 */
export async function reviewGradingConsistencyForTask(
  items: ConsistencyReviewInput[],
  options: { examId?: string },
): Promise<{ adjustments: ConsistencyAdjustment[]; rubricNote?: string }> {
  const examId = options.examId;

  const groups = new Map<string, ConsistencyReviewInput[]>();
  for (const item of items) {
    const list = groups.get(item.dbId);
    if (list) list.push(item);
    else groups.set(item.dbId, [item]);
  }
  // TWO ENTRIES TO COMPARE **AND** SOMETHING THAT MAY MOVE. References count toward the first — that is what they
  // are for — but a group of nothing but references has no adjustment to make and would spend a model call to say so.
  const comparableGroups = Array.from(groups.values()).filter(
    list => list.length >= 2 && list.some(item => item.role !== 'reference'),
  );
  if (comparableGroups.length === 0) return { adjustments: [] };

  let userLanguage = 'English';
  if (examId) {
    try {
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ examId });
      if (conversation && (conversation as any).language) {
        userLanguage = (conversation as any).language;
      }
    } catch (convErr) {
      logger.error(
        { err: convErr, examId: String(examId), context: 'reviewGradingConsistencyForTask' },
        'Failed to fetch conversation for exam',
      );
    }
  }

  const groupLabels = comparableGroups.map(list => list[0]!.nodeLabel);
  const systemPrompt = buildConsistencyReviewSystemPrompt({ groupLabels }, userLanguage);

  const entryById = new Map<string, ConsistencyReviewInput>();
  const groupPayload = comparableGroups.map((list, groupIndex) => {
    const { nodeLabel, criteria, maxPoints } = list[0]!;
    return {
      nodeLabel,
      maxPoints,
      criteria,
      entries: list.map((item, entryIndex) => {
        const entryId = `g${groupIndex}e${entryIndex}`;
        entryById.set(entryId, item);
        return {
          entryId,
          adjustable: item.role !== 'reference',
          score: item.score,
          explanation: item.explanation,
          studentAnswerExcerpt: item.studentAnswerExcerpt.slice(0, CONSISTENCY_REVIEW_ANSWER_EXCERPT_LIMIT),
        };
      }),
    };
  });

  const userPrompt = `Please compare and reconcile the already-graded entries below. Respond in ${userLanguage} where indicated.\n\n${JSON.stringify(
    { groups: groupPayload },
    null,
    2,
  )}`;

  const messages: ChatCompletionMessageParam[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ];

  let analysisResponse: any = null;
  try {
    analysisResponse = await getLlmRouter().chat.completions.create({
      response_format: { type: 'json_object' },
      messages,
    });
    await addCostToExam(analysisResponse, examId, 'consistencyReview');
  } catch (err) {
    logger.error(
      { err, examId: String(examId), context: 'reviewGradingConsistencyForTask' },
      'Consistency review LLM call failed',
    );
    return { adjustments: [] };
  }

  const analysisResult = analysisResponse ? toMessageText(analysisResponse.choices[0]?.message.content) : '';
  interface ConsistencyReviewResult {
    adjustments?: Array<{ entryId?: unknown; grade?: unknown; explanation?: unknown }>;
    rubricNote?: unknown;
  }
  const parsed = safeParseJson<ConsistencyReviewResult>(analysisResult, {});

  const adjustments: ConsistencyAdjustment[] = [];
  for (const raw of parsed.adjustments ?? []) {
    const entryId = String(raw.entryId ?? '');
    const item = entryById.get(entryId);
    if (!item) {
      logger.warn(
        { entryId, context: 'reviewGradingConsistencyForTask' },
        'Consistency reviewer returned an unknown entryId, ignoring',
      );
      continue;
    }
    // ENFORCED HERE, NOT ONLY ASKED FOR IN THE PROMPT. The reviewer is told references are fixed; a model that
    // returns one anyway must not be able to write a grade the teacher never asked to have re-examined.
    if (item.role === 'reference' || !item.gradingResultFieldPath || !item.arrayFilters) {
      logger.info(
        { entryId, submissionId: item.submissionId, context: 'reviewGradingConsistencyForTask' },
        'Consistency reviewer tried to adjust a reference entry — ignoring',
      );
      continue;
    }
    const grade = clampGrade(parseGradeValue(raw.grade), item.maxPoints);
    const explanation =
      typeof raw.explanation === 'string' && raw.explanation.trim() ? raw.explanation : item.explanation;
    adjustments.push({
      submissionId: item.submissionId,
      gradingResultFieldPath: item.gradingResultFieldPath,
      arrayFilters: item.arrayFilters,
      score: grade,
      explanation,
    });
  }

  const rubricNote =
    typeof parsed.rubricNote === 'string' && parsed.rubricNote.trim() ? parsed.rubricNote.trim() : undefined;

  return { adjustments, rubricNote };
}
